<template>
  <div class="text-white relative">
    <!-- 渐变遮罩 -->
    <div
      class="pointer-events-none absolute inset-x-0 bottom-0 h-[5%] z-10"
      style="
        background: linear-gradient(
          to bottom,
          rgba(75, 5, 137, 0) 0%,
          #4b0589 100%
        );
      "
    ></div>
    <!-- 标题区域 -->
    <section class="mb-12 px-4">
      <UPageSection
        title="Choose the Perfect Plan for Your Creativity"
        :ui="{
          container: '!py-4 !px-0 !gap-4',
          title: 'my-6',
        }"
      >
        <div class="flex flex-col items-center gap-8">
          <UTabs
            v-model="billingCycle"
            :items="billingCycleItems"
            :content="false"
            size="sm"
            color="primary"
            variant="pill"
            class="w-auto"
            :ui="{
              trigger:
                'group inline-flex items-center gap-1.5 transition-colors data-[state=active]:text-white',
            }"
          />
          <UPricingPlans>
            <UPricingPlan
              v-for="(plan, index) in plans"
              :key="index"
              v-bind="plan"
            >
              <template #price>
                {{ plan.discount || plan.price
                }}<span class="text-muted text-sm">/month</span>
              </template>
              <template #features>
                <div class="flex flex-col gap-3 flex-1 mt-6 grow-0">
                  <!-- {{ plan }} -->
                  <div
                    v-for="(feature, featureIndex) in plan.features"
                    :key="featureIndex"
                    class="flex items-center gap-2 min-w-0"
                  >
                    <UIcon
                      v-if="!feature.noCheck"
                      name="i-lucide:circle-check"
                      class="size-5 shrink-0 text-primary"
                    />
                    <UIcon
                      v-else
                      name="i-lucide:circle-x"
                      class="size-5 shrink-0 text-muted"
                    />
                    <div class="flex items-center gap-0">
                      <span class="text-muted text-sm truncate">
                        {{ feature.label }}
                      </span>
                      <UPopover
                        v-if="feature.tip"
                        mode="hover"
                        :open-delay="120"
                      >
                        <UIcon
                          name="material-symbols-light:info-outline-rounded"
                          class="ml-1 text-gray-500 cursor-help"
                          style="font-size: 1.1em"
                        />
                        <template #content>
                          <div class="p-2 text-xs max-w-xs">
                            {{ feature.tip }}
                          </div>
                        </template>
                      </UPopover>
                    </div>
                  </div>
                </div>
              </template>
              <template #button>
                <ClientOnly
                  ><UButton
                    v-bind="plan.button"
                    class="w-full max-w-lg justify-center"
                  >
                    {{ plan.button.label }}
                  </UButton>
                  <template #fallback>
                    <div class="w-full max-w-lg justify-center">
                      <USkeleton class="w-full max-w-lg justify-center h-8" />
                    </div>
                  </template>
                </ClientOnly>
              </template>
            </UPricingPlan>
          </UPricingPlans>
        </div>

        <!-- Guarantees -->
        <div class="flex flex-wrap items-center justify-center gap-6 mt-8">
          <div
            class="text-center text-sm text-slate-400 flex items-center justify-center gap-2"
          >
            <UIcon name="i-lucide-badge-check" class="w-4 h-4 text-green-500" />
            7 Days Money Back Guarantee
          </div>

          <div
            class="text-center text-sm text-slate-400 flex items-center justify-center gap-2"
          >
            <UIcon name="i-lucide-badge-check" class="w-4 h-4 text-green-500" />
            Downgrade, upgrade or cancel any time
          </div>

          <div
            class="text-center text-sm text-slate-400 flex items-center justify-center gap-2"
          >
            <UIcon name="i-lucide-badge-check" class="w-4 h-4 text-green-500" />
            <span class="text-muted text-sm truncate"
              >Unused credits will roll over</span
            >
            <UPopover mode="hover" :open-delay="120">
              <UIcon
                name="material-symbols-light:info-outline-rounded"
                class="text-gray-500 cursor-help"
                style="font-size: 1.1em"
              />
              <template #content>
                <div class="p-2 text-xs max-w-xs">
                  Unused credits roll over as long as you're subscribed
                </div>
              </template>
            </UPopover>
          </div>
        </div>
      </UPageSection>
    </section>

    <!-- 图标列表 -->
    <section class="mb-12 px-4">
      <UPageSection
        title="Secure & flexible payment options"
        :ui="{
          container: '!py-4 !px-0 !gap-4',
          title: 'my-6',
        }"
      >
        <!-- 横向排列图标示例 -->
        <div class="flex flex-wrap items-center justify-center gap-8 mt-8">
          <div
            v-for="(icon, index) in iconList"
            :key="index"
            class="flex items-center justify-center"
          >
            <img
              :src="icon.src"
              :alt="icon.alt"
              class="h-8 w-auto object-contain hover:opacity-80 transition-opacity duration-200"
            />
          </div>
        </div>
      </UPageSection>
    </section>

    <!-- 安全、支持、保障 -->
    <section class="mb-12 px-4">
      <UPageSection
        title="Safe, Secure, and Supported"
        description="Your creativity is protected, and our team is here to help — anytime."
        :features="paymentFeatures"
        :ui="{
          container: '!py-4 !px-0',
        }"
      />
    </section>

    <!-- FAQ -->
    <section class="mb-12 px-4">
      <UPageSection
        title="Pricing FAQs"
        :ui="{
          container: '!py-4 !px-0',
        }"
      >
        <UAccordion
          :items="faqs"
          :default-value="['0']"
          :ui="{
            trigger: 'text-[14px]',
            body: 'text-[14px] text-muted whitespace-pre-line',
          }"
        />
      </UPageSection>
    </section>

    <!-- 底部 -->
    <section class="py-48 px-4">
      <UPageSection
        title="Start Creating Stunning Media Today"
        description="Create your account and get free credits"
        :ui="{
          container: '!py-4 !px-0 !gap-4',
        }"
      >
        <div class="flex flex-col items-center justify-center gap-3">
          <!-- 已登录时显示视频按钮 -->
          <div v-if="token" class="flex flex-col items-center mt-6">
            <div
              @click="$router.push('/ai/video/text-to-video/')"
              class="relative cursor-pointer overflow-hidden group aspect-149/29"
              style="width: 300px; border-radius: 8px"
            >
              <div
                class="absolute inset-0 w-full h-full"
                style="border-radius: 8px; overflow: hidden"
              >
                <video
                  class="object-cover pointer-events-none"
                  :src="`${cdnUrl}/ai/public/home/home-banner-bg-video.mp4`"
                  autoplay
                  muted
                  loop
                  playsinline
                  style="object-fit: cover"
                />
              </div>
              <div
                class="absolute inset-0 bg-black/40 group-hover:bg-white transition-colors duration-300"
                style="border-radius: 8px"
              ></div>
              <div
                class="relative z-10 flex items-center justify-center h-full w-full"
              >
                <span
                  class="font-bold text-white text-xl group-hover:text-[#4b0589] transition-colors duration-300"
                  style="letter-spacing: 0.01em"
                >
                  Create Videos with Veo 3.1
                </span>
              </div>
            </div>
          </div>
          <!-- 未登录时显示原有按钮 -->
          <template v-else>
            <UButton
              size="xl"
              class="w-full max-w-lg justify-center"
              color="neutral"
              @click="openSignUpModal()"
            >
              <span class="w-full block text-center text-[#6E28FD] font-bold"
                >Sign Up Free</span
              >
            </UButton>
            <span class="w-full max-w-lg block text-center"
              >No credit card required for Free Plan. Upgrade anytime.</span
            >
          </template>
        </div>
      </UPageSection>
    </section>

    <StripeCheckoutModal
      v-model:open="stripeCheckoutOpen"
      :checkout-url="stripeCheckoutUrl"
      consumer-key="pricing"
      @completed="onStripeCheckoutCompleted"
      @failed="onStripeCheckoutFailed"
    />
  </div>
</template>

<script setup>
import { useStore } from "vuex";
import { useRuntimeConfig } from "#app";
import { PricingService } from "@/services";
import { getItem } from "@/common/storage";
import StripeCheckoutModal from "@/components/payments/StripeCheckoutModal.vue";

definePageMeta({ layout: "sign-new" });

const route = useRoute();

const cdnUrl = useRuntimeConfig().public.cdnUrl;
useHead({
  title: "Plan & Pricing | SharkFoto",
  link: [{ rel: "canonical", href: "/pricing/" }],
  meta: [
    {
      hid: "description",
      name: "description",
      content:
        "Unclock SharkFoto all features with subscription. Unlimited previews and download, batch editing and more. Sign up free now!",
    },
    { hid: "og:site_name", property: "og:site_name", content: "SharkFoto" },
    { hid: "og:type", property: "og:type", content: "website" },
    { hid: "og:locale", property: "og:locale", content: "en_US" },
    {
      hid: "og:image",
      property: "og:image",
      content: `${cdnUrl}/sharkfoto_og.png`,
    },
    {
      hid: "og:url",
      property: "og:url",
      content: "https://sharkfoto.com/pricing/",
    },
    {
      hid: "og:title",
      property: "og:title",
      content: "Plan & Pricing | SharkFoto",
    },
    {
      hid: "og:description",
      property: "og:description",
      content:
        "Unclock SharkFoto all features with subscription. Unlimited previews and download, batch editing and more. Sign up free now!",
    },
    {
      hid: "twitter:card",
      name: "twitter:card",
      content: "summary_large_image",
    },
    {
      hid: "twitter:url",
      name: "twitter:url",
      content: "https://sharkfoto.com/pricing/",
    },
    {
      hid: "twitter:title",
      name: "twitter:title",
      content: "Plan & Pricing | SharkFoto",
    },
    {
      hid: "twitter:image",
      name: "twitter:image",
      content: `${cdnUrl}/sharkfoto_og.png`,
    },
    { hid: "twitter:site", name: "twitter:site", content: "@Shark_Foto" },
    {
      hid: "twitter:description",
      name: "twitter:description",
      content:
        "Unclock SharkFoto all features with subscription. Unlimited previews and download, batch editing and more. Sign up free now!",
    },
  ],
});

function firstQueryValue(val) {
  if (val == null) return "";
  return Array.isArray(val) ? val[0] : val;
}

/** 从地址栏解析计费周期，缺省为包年 */
function normalizeBillingCycleFromQuery(query) {
  const raw = String(
    (query &&
      (firstQueryValue(query.cycle) || firstQueryValue(query.interval))) ||
      ""
  ).toLowerCase();
  if (raw === "monthly" || raw === "month" || raw === "m") {
    return "monthly";
  }
  if (raw === "yearly" || raw === "year" || raw === "annual" || raw === "y") {
    return "yearly";
  }
  return "yearly";
}

/** Stripe 成功/取消回跳：只带 cycle，与页面上 Monthly/Yearly 一致 */
function buildSubscriptionCheckoutReturnUrl(cycleValue, consumer = "pricing") {
  if (!import.meta.client) {
    return "";
  }
  const origin = window.location.origin;
  const path = window.location.pathname || "/pricing/";
  const u = new URL(origin + path);
  u.searchParams.set("cycle", cycleValue);
  u.searchParams.set("stripe_checkout_consumer", consumer);
  return u.toString();
}

/** 登录页 ?redirect= 使用 pathname+search，与旧版 /pricing/ 形式一致 */
function buildSignInRedirectPricingPath(cycleValue) {
  const full = buildSubscriptionCheckoutReturnUrl(cycleValue);
  if (!full) {
    return `/pricing/?cycle=${encodeURIComponent(cycleValue)}`;
  }
  try {
    const u = new URL(full);
    return `${u.pathname}${u.search}`;
  } catch {
    return `/pricing/?cycle=${encodeURIComponent(cycleValue)}`;
  }
}

const billingCycle = ref(normalizeBillingCycleFromQuery(route.query));

watch(
  () => [route.query.cycle, route.query.interval],
  () => {
    billingCycle.value = normalizeBillingCycleFromQuery(route.query);
  },
  { immediate: true }
);

// 计费周期选项
const billingCycleItems = [
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

// 支持的支付方式图标列表
const iconList = [
  {
    src: `${cdnUrl}/ai/public/svg/visa.svg`,
    alt: "Visa",
  },
  {
    src: `${cdnUrl}/ai/public/svg/master.svg`,
    alt: "Mastercard",
  },
  {
    src: `${cdnUrl}/ai/public/svg/ame.svg`,
    alt: "American Express",
  },
  {
    src: `${cdnUrl}/ai/public/svg/discover.svg`,
    alt: "Discover",
  },
  {
    src: `${cdnUrl}/ai/public/svg/jcb.svg`,
    alt: "JCB",
  },
  {
    src: `${cdnUrl}/ai/public/svg/maestro.svg`,
    alt: "Maestro",
  },
  {
    src: `${cdnUrl}/ai/public/svg/unionpay.svg`,
    alt: "UnionPay",
  },
  {
    src: `${cdnUrl}/ai/public/svg/paypal.svg`,
    alt: "PayPal",
  },
  {
    src: `${cdnUrl}/ai/public/svg/googlepay.svg`,
    alt: "Google Pay",
  },
  {
    src: `${cdnUrl}/ai/public/svg/applepay.svg`,
    alt: "Apple Pay",
  },
  {
    src: `${cdnUrl}/ai/public/svg/bank.svg`,
    alt: "Bank Transfer",
  },
];

// 用户信息
const store = useStore();
const token = computed(() => store.state.user.token);
const userInfo = computed(() => store.state.user.userInfo);
const subscribe = computed(() => store.state.common.subscribe);

const getUserInfo = (payload) => store.dispatch("user/getUserInfo", payload);

// pending_cancel 状态
const pendingCancel = computed(
  () => Number(userInfo.value?.pending_cancel || 0) === 1
);

// 订阅类型等级: "Free", "Personal", "Professional"
const userSubscribe = computed(() => userInfo.value.product_name);
// 订阅周期等级: "Monthly", "Yearly"
const userSubscribeInterval = computed(() => userInfo.value.recurring_interval);

const buyLoading = ref(false);
const userInfoLoading = computed(() => store.state.user.userInfoLoading);

const stripeCheckoutOpen = ref(false);
const stripeCheckoutUrl = ref("");

const __PRO__ = __CUSTOM_ENV__ === "production";

// 订阅等级映射
const subscriptionLevel = {
  Free: 0,
  Personal: 1,
  Professional: 2,
};

// 控制是否禁用低于当前等级的订阅选项
const disableLowerPlans = ref(false);

const normalizePlanType = (value) => String(value || "").toLowerCase();
const normalizeCycleValue = (value) => {
  const raw = String(value || "").toLowerCase();
  if (raw === "month" || raw === "monthly") return "monthly";
  if (raw === "year" || raw === "yearly" || raw === "annual") {
    return "yearly";
  }
  return raw;
};

const getCurrentSubscriptionSnapshot = () => ({
  planType: normalizePlanType(userSubscribe.value || "Free"),
  billingCycle: normalizeCycleValue(userSubscribeInterval.value),
});

const isAllowedDowngradeTarget = (targetPlanType, targetCycle) => {
  const current = getCurrentSubscriptionSnapshot();
  return (
    current.planType === "professional" &&
    normalizePlanType(targetPlanType) === "personal" &&
    current.billingCycle !== "" &&
    current.billingCycle === normalizeCycleValue(targetCycle)
  );
};

const isUpgradeTarget = (targetPlanType, targetCycle) => {
  const current = getCurrentSubscriptionSnapshot();
  const currentPlanLevel = subscriptionLevel[userSubscribe.value || "Free"] || 0;
  const normalizedTargetPlan = normalizePlanType(targetPlanType);
  const normalizedTargetCycle = normalizeCycleValue(targetCycle);

  if (!current.billingCycle) {
    return false;
  }

  const targetKey =
    normalizedTargetPlan === "professional"
      ? "Professional"
      : normalizedTargetPlan === "personal"
        ? "Personal"
        : "Free";
  const targetPlanLevel = subscriptionLevel[targetKey] || 0;

  if (normalizedTargetPlan === current.planType) {
    return current.billingCycle === "monthly" && normalizedTargetCycle === "yearly";
  }

  return targetPlanLevel > currentPlanLevel;
};

const getPlanActionType = (targetPlanType, targetCycle) => {
  if (isAllowedDowngradeTarget(targetPlanType, targetCycle)) {
    return "downgrade";
  }
  if (isUpgradeTarget(targetPlanType, targetCycle)) {
    return "upgrade";
  }
  return "unsupported";
};

// 判断是否是当前订阅
const isCurrentSubscription = computed(() => {
  return (planType, cycle) => {
    // 如果 pending_cancel 为1，视为 free 用户，所有套餐都可以选择
    if (pendingCancel.value) return false;

    const currentPlan = userSubscribe.value || "Free";
    const currentCycle = userSubscribeInterval.value || "";

    // 如果计划类型和周期都匹配，则是当前订阅
    return (
      planType.toLowerCase() === currentPlan.toLowerCase() &&
      cycle.toLowerCase() === currentCycle.toLowerCase()
    );
  };
});

// 判断是否低于当前订阅等级
const isLowerThanCurrent = computed(() => {
  return (planType, cycle) => {
    // 如果 pending_cancel 为1，视为 free 用户，所有套餐都可以选择
    if (pendingCancel.value) return false;

    // 如果配置为不禁用低级计划，直接返回 false
    if (!disableLowerPlans.value) {
      return false;
    }

    const currentPlan = userSubscribe.value || "Free";
    const currentPlanLevel = subscriptionLevel[currentPlan] || 0;
    const targetPlanLevel = subscriptionLevel[planType] || 0;

    // 如果目标计划类型等级低于当前，则禁用
    if (targetPlanLevel < currentPlanLevel) {
      return !isAllowedDowngradeTarget(planType, cycle);
    }

    return false;
  };
});

// 使用全局认证模态框
const { openSignUpModal, openSignInModal } = useAuthModals();

// Stripe 价格配置（test 已配置，live 预留待填）
const pricingPlans = {
  personal: {
    localProductId: {
      monthly: 804215,
      yearly: 804216,
    },
    priceId: {
      monthly: !__PRO__
        ? "price_1TMIHcLb6N7Fd5AC4VbPczbw"
        : "price_1TMjrRLOAzmVXp5dAv56319V",
      yearly: !__PRO__
        ? "price_1TMIOwLb6N7Fd5ACosL8BaFL"
        : "price_1TMjrRLOAzmVXp5d6jZo7Vzc",
    },
  },
  professional: {
    localProductId: {
      monthly: 804217,
      yearly: 804218,
    },
    priceId: {
      monthly: !__PRO__
        ? "price_1TMIR2Lb6N7Fd5ACUJIXG2ZI"
        : "price_1TMjrtLOAzmVXp5d3dTxlNsT",
      yearly: !__PRO__
        ? "price_1TMITsLb6N7Fd5AC65xdsZGe"
        : "price_1TMjtqLOAzmVXp5dT7ov6o7a",
    },
  },
};

//
const isYearly = computed(() => billingCycle.value === "yearly");
const cycle = computed(() => billingCycle.value);

// 为每个套餐计算当前状态（避免重复计算）
const getPersonalStatus = computed(() => {
  const isCurrent = isCurrentSubscription.value("Personal", cycle.value);
  const isLower = isLowerThanCurrent.value("Personal", cycle.value);
  const actionType = hasActiveSubscription.value
    ? getPlanActionType("Personal", cycle.value)
    : "new";
  const isUnsupported =
    hasActiveSubscription.value && !isCurrent && actionType === "unsupported";
  return { isCurrent, isLower, actionType, isUnsupported };
});

const getProfessionalStatus = computed(() => {
  const isCurrent = isCurrentSubscription.value("Professional", cycle.value);
  const isLower = isLowerThanCurrent.value("Professional", cycle.value);
  const actionType = hasActiveSubscription.value
    ? getPlanActionType("Professional", cycle.value)
    : "new";
  const isUnsupported =
    hasActiveSubscription.value && !isCurrent && actionType === "unsupported";
  return { isCurrent, isLower, actionType, isUnsupported };
});

// 使用计算属性根据billingCycle动态生成价格
const plans = computed(() => {
  const personalStatus = getPersonalStatus.value;
  const professionalStatus = getProfessionalStatus.value;

  return [
    {
      title: "Free",
      description: "Registered users with free plan",
      price: "$0",
      features: [
        {
          label: "No Watermark",
          noCheck: true,
        },
        {
          label: "Commercial License",
          noCheck: true,
        },
        {
          label: "1G Storage",
        },
        {
          label: "10 free credit",
          tip: "You will get 10 credit for free every month if your credit is used out. Normally, on the 1st of the month, we will update your account and you will get an email notification.",
        },
        {
          label: "10+ free image tools and batch editing",
        },
        {
          label: "Refer friends and earn credits",
          tip: "You'll receive 30 credits when the person you invite signs up for a SharkFoto account, and they'll also get 30 extra credits to get started (on top of the 10 credit for signing up). Win-Win!",
        },
      ],
      button: {
        label: !token.value ? "Sign Up Free" : "My Account",
        color: "neutral",
        onClick: () => {
          if (!token.value) {
            // 打开登录弹窗
            openSignUpModal();
          } else {
            navigateTo("/my/plan-credits", { target: "_blank" });
          }
        },
      },
    },
    {
      title: "Personal",
      description: "Recommend for personal",
      price: "$10.00",
      discount: isYearly.value ? "$9.00" : "",
      features: [
        {
          label: "No Watermark",
        },
        {
          label: "Commercial License",
        },
        {
          label: "5G Storage",
        },
        {
          label: isYearly.value
            ? "500 credits per month ($0.018/credit)"
            : "500 credits per month ($0.02/credit)",
        },
        {
          label: "10+ free image tools and batch editing",
        },
        {
          label: "Refer friends and earn credits",
        },
      ],
      button: {
        label: personalStatus.isCurrent
          ? "Current Plan"
          : personalStatus.isUnsupported
            ? "Unavailable"
          : personalStatus.actionType === "downgrade"
            ? "Downgrade"
          : personalStatus.actionType === "upgrade"
            ? "Upgrade"
            : "Buy Now",
        class: `text-white`,
        disabled:
          personalStatus.isCurrent ||
          personalStatus.isLower ||
          personalStatus.isUnsupported,
        loading:
          (buyLoading.value || userInfoLoading.value) && token.value !== "",
        onClick: () => {
          if (!token.value) {
            openSignUpModal();
          } else if (!personalStatus.isCurrent && !personalStatus.isLower) {
            handleSubscribe("personal");
          }
        },
      },
    },
    {
      title: "Professional",
      description: "Recommend for pro users",
      price: "$30.00",
      discount: isYearly.value ? "$27.00" : "",
      features: [
        {
          label: "No Watermark",
        },
        {
          label: "Commercial License",
        },
        {
          label: "10G Storage",
        },
        {
          label: isYearly.value
            ? "2,000 credits per month ($0.0135/credit)"
            : "2,000 credits per month ($0.015/credit)",
        },
        {
          label: "10+ free image tools and batch editing",
        },
        {
          label: "Refer friends and earn credits",
        },
      ],
      button: {
        label: professionalStatus.isCurrent
          ? "Current Plan"
          : professionalStatus.isUnsupported
            ? "Unavailable"
          : professionalStatus.actionType === "upgrade"
            ? "Upgrade"
            : "Buy Now",
        class: `text-white`,
        disabled:
          professionalStatus.isCurrent ||
          professionalStatus.isLower ||
          professionalStatus.isUnsupported,
        loading:
          (buyLoading.value || userInfoLoading.value) && token.value !== "",
        onClick: () => {
          if (!token.value) {
            openSignUpModal();
          } else if (
            !professionalStatus.isCurrent &&
            !professionalStatus.isLower
          ) {
            handleSubscribe("professional");
          }
        },
      },
    },
  ];
});

// 安全、支持、保障
const paymentFeatures = ref([
  {
    title: "Secure",
    description:
      "We protect your creations and personal information with advanced encryption and privacy measures.",
    icon: "material-symbols-light:health-and-safety-outline-rounded",
    ui: {
      leadingIcon: "size-20 shrink-0 text-primary",
    },
  },
  {
    title: "Support",
    description:
      "Get fast help from our advisors via live chat or email within 24 hours, every day.",
    icon: "material-symbols-light:headphones-outline-sharp",
    ui: {
      leadingIcon: "size-20 shrink-0 text-primary",
    },
  },
  {
    title: "Money-back Guarantee",
    description:
      "Enjoy a no-hassle 14-day money-back guarantee on all SharkFoto products — satisfaction guaranteed.",
    icon: "material-symbols-light:attach-money-rounded",
    ui: {
      leadingIcon: "size-20 shrink-0 text-primary",
    },
  },
]);

// FAQ
const faqs = [
  {
    label: "What can we do with a free account?",
    content:
      "With a free SharkFoto account, you receive 10 free credits every month. You can use these credits to try out our advanced AI models for image, video, and audio generation. Additionally, you get access to 10+ free image utility tools (such as Background Remover, Image Upscaler, and Color Enhancer), batch editing capabilities, and 1GB of cloud storage. All outputs on the free plan come with no watermarks and include a commercial license.",
    value: "1",
  },
  {
    label: "What are credits and how are they deducted?",
    content:
      "Credits are the currency used on SharkFoto to generate and edit media using our AI models. Your credits are deducted based on several factors, including the specific AI model you choose (e.g., Sora 2, Veo 3.1, Kling O3), the type of generation (image, video, or audio), the duration of videos, and the resolution of the outputs.",
    value: "2",
  },
  {
    label: "Do unused credits roll over to the next billing cycle?",
    content:
      "Yes! Unlike many other platforms, SharkFoto allows your unused subscription credits to roll over to the next billing cycle, as long as your subscription remains active. This ensures you never lose the credits you've paid for.",
    value: "3",
  },
  {
    label: "What happens if I run out of credits?",
    content:
      "If you run out of credits before your next billing cycle, you have a few options. You can upgrade your current subscription to a higher tier (such as moving from Personal to Professional) to receive a larger monthly credit allowance. Alternatively, you can earn additional free credits by referring friends to SharkFoto.",
    value: "4",
  },
  {
    label: "Can I cancel, upgrade, or downgrade my subscription?",
    content:
      "Absolutely. You have full control over your subscription and can cancel, upgrade, or downgrade your plan at any time directly from your account settings. Changes to your plan will take effect at the start of your next billing cycle.",
    value: "5",
  },
  {
    label: "What payment methods do you support?",
    content:
      "We securely process payments through Stripe and accept various payment methods, including major credit cards (Visa, MasterCard, American Express, etc.) and other standard online payment options supported by Stripe in your region.",
    value: "6",
  },
  {
    label: "What is your refund policy?",
    content:
      "You can request a refund for subscription credits within 7 days of your purchase, provided you have not used more than 50 subscription credits. Please note that add-on credits are non-refundable. For full details, please review our Refund Policy.",
    value: "7",
  },
  {
    label: "Is Sharkfoto.com GDPR compliant?",
    content:
      "Yes, SharkFoto is fully GDPR compliant. We prioritize your privacy and data security, protecting your personal information and creations with advanced encryption measures. For more information on how we handle your data, please read our Privacy Policy.",
    value: "8",
  },
];

// 支付相关
const toast = useToast();

// 检查用户是否已有订阅
const hasActiveSubscription = computed(() => {
  return (
    userInfo.value?.subscription_id &&
    userInfo.value?.product_id &&
    Number(userInfo.value.product_id) !== 0
  );
});

const openStripeCheckout = (sessionData) => {
  stripeCheckoutUrl.value = sessionData?.checkout_url || "";
  if (!stripeCheckoutUrl.value) {
    throw new Error("Stripe checkout session data invalid");
  }
  stripeCheckoutOpen.value = true;
};

const onStripeCheckoutCompleted = async () => {
  stripeCheckoutOpen.value = false;
  buyLoading.value = false;
  stripeCheckoutUrl.value = "";
  try {
    const uid = userInfo.value?.user_info_id;
    if (uid) {
      await getUserInfo({ user_id: uid });
    }
  } catch (e) {
    console.error("getUserInfo after checkout:", e);
  }
  toast.add({
    title: "Success",
    description: "Payment successful!",
    color: "success",
    icon: "i-lucide-check-circle",
  });
};

const onStripeCheckoutFailed = (message) => {
  stripeCheckoutOpen.value = false;
  buyLoading.value = false;
  stripeCheckoutUrl.value = "";
  toast.add({
    title: "Error",
    description: message || "Failed to start Stripe checkout.",
    color: "error",
    icon: "i-lucide-x-circle",
  });
};

const downgradeSubscription = async (params) => {
  if (!hasActiveSubscription.value) {
    return;
  }

  try {
    const downgradeParams = {
      subscription_id: userInfo.value.subscription_id,
      user_id: userInfo.value.user_info_id,
      current_product_id: userInfo.value.product_id,
      product_id: params.productId,
      price_id: params.priceId,
    };

    const result = await PricingService.downgradeSubscribe(
      userInfo.value.user_info_id,
      downgradeParams
    );

    if (result?.status === 200) {
      await getUserInfo({ user_id: userInfo.value.user_info_id });
      buyLoading.value = false;
      toast.add({
        title: "Success",
        description:
          "Subscription downgraded successfully. Your existing credits remain available.",
        color: "success",
        icon: "i-lucide-check-circle",
      });
    } else {
      throw new Error(result?.message || "降级订阅失败");
    }
  } catch (error) {
    buyLoading.value = false;
    toast.add({
      title: "Error",
      description:
        error.message || "Failed to downgrade subscription. Please try again.",
      color: "error",
      icon: "i-lucide-x-circle",
    });
  }
};

// 更新订阅（已有订阅时使用）
const updateSubscription = async (params) => {
  if (!hasActiveSubscription.value) {
    return;
  }

  try {
    const updateParams = {
      subscription_id: userInfo.value.subscription_id,
      user_id: userInfo.value.user_info_id,
      product_id: params.productId,
      price_id: params.priceId,
    };
    const result = await PricingService.updateSubscribe(
      userInfo.value.user_info_id,
      updateParams
    );

    if (result?.status === 200) {
      console.log("订阅更新成功, 准备调用 getUserInfo");
      await getUserInfo({ user_id: userInfo.value.user_info_id });
      buyLoading.value = false;
      toast.add({
        title: "Success",
        description: "Subscription updated successfully!",
        color: "success",
        icon: "i-lucide-check-circle",
      });
    } else {
      throw new Error(result?.message || "更新订阅失败");
    }
  } catch (error) {
    buyLoading.value = false;
    toast.add({
      title: "Error",
      description:
        error.message || "Failed to update subscription. Please try again.",
      color: "error",
      icon: "i-lucide-x-circle",
    });
  }
};

// 订阅支付（创建新订阅或更新订阅）
const onCallPayment = async (params) => {
  if (!token.value) {
    // 登录已弹窗化:旧 /sign-in 页已 301,改开登录弹窗(登录后回到定价页对应档)。
    const back = buildSignInRedirectPricingPath(params.billingCycle);
    openSignInModal(back);
    return;
  }

  // 如果用户已有订阅，使用更新订阅 API
  if (hasActiveSubscription.value) {
    const actionType = getPlanActionType(params.planType, params.billingCycle);

    if (actionType === "downgrade") {
      await downgradeSubscription(params);
      return;
    }

    if (actionType === "upgrade") {
      await updateSubscription(params);
      return;
    }

    throw new Error("This subscription change is not supported.");
    return;
  }

  const result = await getUserInfo({ user_id: userInfo.value.user_info_id });

  if (result && result.status === 403) {
    store.commit("common/SET_SUBSCRIBE", {
      planType: params.planType,
      billingCycle: params.billingCycle,
    });
    return;
  }

  if (!params.priceId) {
    throw new Error("Stripe live price id is not configured.");
  }

  const sessionRes = await PricingService.createSubscriptionCheckoutSession(
    userInfo.value.user_info_id,
    {
      price_id: params.priceId,
      product_id: params.productId,
      current_url: buildSubscriptionCheckoutReturnUrl(params.billingCycle),
    }
  );
  if (sessionRes?.status !== 200) {
    throw new Error(sessionRes?.message || "Create checkout session failed");
  }
  if (!sessionRes?.data?.checkout_url) {
    throw new Error("Checkout session missing checkout url");
  }
  openStripeCheckout(sessionRes.data);
};

const replayPendingSubscription = async () => {
  const sub = subscribe.value || getItem("subscribe") || null;
  if (!sub || !token.value) return;
  const planType = sub.planType;
  const billingCycleValue = sub.billingCycle;
  if (!planType || !billingCycleValue) {
    store.commit("common/SET_SUBSCRIBE", null);
    return;
  }
  store.commit("common/SET_SUBSCRIBE", null);
  await handleSubscribe(planType, billingCycleValue);
};

// 处理订阅
const handleSubscribe = async (planType, overrideCycle = null) => {
  buyLoading.value = true;

  try {
    const cycleValue = overrideCycle || billingCycle.value;
    const selectedConfig = {
      planType,
      billingCycle: cycleValue,
      productId: pricingPlans[planType].localProductId[cycleValue],
      priceId: pricingPlans[planType].priceId[cycleValue],
    };
    await onCallPayment(selectedConfig);
  } catch (error) {
    toast.add({
      title: "Error",
      description: error?.message || "Payment failed, please try again.",
      color: "error",
      icon: "i-lucide-x-circle",
    });
  } finally {
    buyLoading.value = false;
  }
};

onMounted(() => {
  replayPendingSubscription();
});

watch([token, subscribe], () => {
  replayPendingSubscription();
});

watch(stripeCheckoutOpen, (open) => {
  if (!open) {
    // 用户关闭支付弹窗或支付流程结束时，确保按钮停止 loading
    buyLoading.value = false;
  }
});
</script>
